export interface SortCondition {
  field: string;
  direction: "asc" | "desc";
}

/**
 * 정렬 문자열을 파싱하여 정렬 조건 배열로 변환
 * @param sortString "field1 asc, field2 desc" 형태의 문자열
 * @returns SortCondition 배열
 */
export function parseSortString(sortString: string): SortCondition[] {
  if (!sortString || typeof sortString !== "string") {
    return [];
  }

  try {
    return sortString
      .split(",")
      .map((condition) => condition.trim())
      .filter((condition) => condition.length > 0)
      .map((condition) => {
        const parts = condition.split(/\s+/);
        if (parts.length < 2) {
          throw new Error(`Invalid sort condition: ${condition}`);
        }

        const field = parts[0];
        const direction = parts[1].toLowerCase();

        if (direction !== "asc" && direction !== "desc") {
          throw new Error(
            `Invalid sort direction: ${direction}. Must be 'asc' or 'desc'`
          );
        }

        return {
          field,
          direction: direction as "asc" | "desc",
        };
      });
  } catch (error) {
    console.warn("Failed to parse sort string:", sortString, error);
    return [];
  }
}

/**
 * 시간 문자열 비교 (HH:MM 형태)
 * @param timeA 시간 문자열 A
 * @param timeB 시간 문자열 B
 * @returns 비교 결과 (-1, 0, 1)
 */
function compareTime(timeA: string, timeB: string): number {
  if (!timeA && !timeB) return 0;
  if (!timeA) return -1;
  if (!timeB) return 1;

  // HH:MM 형태는 문자열 비교로도 시간순 정렬 가능
  return timeA.localeCompare(timeB);
}

/**
 * 두 값을 비교하여 정렬 순서 반환
 * @param valueA 값 A
 * @param valueB 값 B
 * @param fieldName 필드명 (시간 필드 특별 처리용)
 * @returns 비교 결과 (-1, 0, 1)
 */
function compareValues(valueA: any, valueB: any, fieldName: string): number {
  // 시간 필드 특별 처리
  if (fieldName.toLowerCase().includes("time")) {
    return compareTime(String(valueA || ""), String(valueB || ""));
  }

  // null/undefined 처리
  if (valueA == null && valueB == null) return 0;
  if (valueA == null) return -1;
  if (valueB == null) return 1;

  // 숫자 비교
  if (typeof valueA === "number" && typeof valueB === "number") {
    return valueA - valueB;
  }

  // 문자열 비교
  return String(valueA).localeCompare(String(valueB));
}

/**
 * 배열을 다중 조건으로 정렬
 * @param array 정렬할 배열
 * @param conditions 정렬 조건 배열
 * @returns 정렬된 새 배열
 */
export function sortArray<T extends Record<string, any>>(
  array: T[],
  conditions: SortCondition[]
): T[] {
  if (!Array.isArray(array) || array.length === 0) {
    return array;
  }

  if (!conditions || conditions.length === 0) {
    return [...array];
  }

  return [...array].sort((a, b) => {
    for (const condition of conditions) {
      const { field, direction } = condition;

      // 필드가 객체에 존재하는지 확인
      if (!(field in a) || !(field in b)) {
        console.warn(`Sort field '${field}' does not exist in object`);
        continue;
      }

      const valueA = a[field];
      const valueB = b[field];

      const compareResult = compareValues(valueA, valueB, field);

      if (compareResult !== 0) {
        return direction === "asc" ? compareResult : -compareResult;
      }
    }

    return 0;
  });
}

/**
 * 정렬 문자열을 사용하여 배열 정렬 (편의 함수)
 * @param array 정렬할 배열
 * @param sortString "field1 asc, field2 desc" 형태의 정렬 문자열
 * @returns 정렬된 새 배열
 */
export function sortArrayByString<T extends Record<string, any>>(
  array: T[],
  sortString: string
): T[] {
  const conditions = parseSortString(sortString);
  return sortArray(array, conditions);
}
